import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Mail, Plus, Sparkles, Trash2 } from "lucide-react";

const SAMPLE = {
  from_name: "Alex from Nike",
  from_email: "alex@nike.com",
  subject: "Summer Reels campaign — pegasus 41s",
  body:
    "Hey! We loved your last workout Reel and would love to send you 5 pairs of Pegasus 41s for a 3-Reel deal over June. Budget is flexible — what's your rate for something like this? Turnaround ~2 weeks.\n\nAlex\nInfluencer Marketing, Nike",
};

const relative = (iso) => {
  const d = new Date(iso);
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export default function Inbox() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0, address: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_name: "", from_email: "", subject: "", body: "" });

  const load = () => api.get("/inbox").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.address);
      toast.success("Copied.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inbox/manual", form);
      setForm({ from_name: "", from_email: "", subject: "", body: "" });
      setOpen(false);
      load();
      toast.success("Pitch added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const tryIt = async () => {
    try {
      await api.post("/inbox/manual", SAMPLE);
      load();
      toast.success("Sample pitch added — click 'Draft reply' to see the Copilot.");
    } catch (err) {
      toast.error("Failed to seed sample");
    }
  };

  const remove = async (id) => {
    await api.delete(`/inbox/${id}`);
    load();
  };

  const draftReply = (item) => {
    nav(`/app/copilot?inbox_id=${item.id}`);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">// inbox</p>
          <h1 className="font-display font-black text-5xl tracking-tighter">Pitches, incoming.</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-full border border-white/15"
                data-testid="inbox-add-btn"
              >
                <Plus className="w-4 h-4" /> Paste a pitch
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-white/10">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">Paste a brand pitch</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="inbox-manual-form">
                <Input
                  placeholder="From name (e.g. Alex from Nike)"
                  value={form.from_name}
                  onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                  className="bg-[#0e0e0e] border-white/10"
                  data-testid="inbox-from-name-input"
                />
                <Input
                  placeholder="From email"
                  value={form.from_email}
                  onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                  className="bg-[#0e0e0e] border-white/10"
                  data-testid="inbox-from-email-input"
                />
                <Input
                  placeholder="Subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  required
                  className="bg-[#0e0e0e] border-white/10"
                  data-testid="inbox-subject-input"
                />
                <Textarea
                  rows={8}
                  placeholder="Paste the email body…"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  required
                  className="bg-[#0e0e0e] border-white/10"
                  data-testid="inbox-body-textarea"
                />
                <Button
                  type="submit"
                  className="w-full rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
                  data-testid="inbox-manual-submit"
                >
                  Add to inbox
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Address card */}
      <div className="p-6 rounded-2xl bg-[#141414] border border-white/5 mb-8" data-testid="inbox-address-card">
        <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// your dap inbox</p>
        <div className="flex items-center gap-3 flex-wrap">
          <Mail className="w-4 h-4 text-neutral-400" />
          <code
            className="font-mono text-lg text-white bg-[#0e0e0e] px-3 py-2 rounded-lg border border-white/10 break-all"
            data-testid="inbox-address-display"
          >
            {data.address || "…"}
          </code>
          <Button size="sm" variant="ghost" onClick={copy} data-testid="inbox-copy-btn" className="border border-white/10">
            <Copy className="w-3 h-3" /> Copy
          </Button>
        </div>
        <details className="mt-4 text-sm text-neutral-400">
          <summary className="cursor-pointer hover:text-white select-none">How to auto-forward brand emails →</summary>
          <div className="mt-3 space-y-2 pl-4 border-l border-white/10">
            <p>
              1. In Gmail: <span className="font-mono">Settings → Forwarding and POP/IMAP → Add a forwarding address</span>
            </p>
            <p>
              2. Paste your DAP inbox address above. Confirm the code Gmail emails you.
            </p>
            <p>
              3. Create a filter for brand keywords (<span className="font-mono">"collab", "partnership", "sponsored"</span>) → forward to DAP.
            </p>
            <p className="text-neutral-500 italic">
              Not deployed yet? No problem — use "Paste a pitch" for now. Forwarding activates once you deploy to your dap.app domain.
            </p>
          </div>
        </details>
      </div>

      {/* List */}
      {data.items.length === 0 ? (
        <div className="p-12 rounded-2xl bg-[#141414] border border-dashed border-white/10 text-center">
          <p className="font-display font-black text-3xl tracking-tighter mb-2">No pitches yet.</p>
          <p className="text-neutral-400 mb-6">Forward one from Gmail — or seed a sample to see the Copilot flow.</p>
          <Button
            onClick={tryIt}
            className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
            data-testid="inbox-try-sample-btn"
          >
            <Sparkles className="w-4 h-4" /> Try it with a sample pitch
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-list">
          {data.items.map((it) => (
            <div
              key={it.id}
              className={`p-5 rounded-2xl border transition-colors ${
                it.read ? "bg-[#141414] border-white/5" : "bg-[#141414] border-[#CCFF00]/30"
              }`}
              data-testid={`inbox-item-${it.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {!it.read && <span className="w-2 h-2 rounded-full bg-[#CCFF00]" />}
                    <p className="font-display font-bold text-lg truncate">{it.subject}</p>
                  </div>
                  <p className="text-sm text-neutral-400">
                    {it.from_name ? `${it.from_name} · ` : ""}
                    <span className="font-mono">{it.from_email || "unknown"}</span> · {relative(it.created_at)}
                    {it.source === "webhook" && (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/5">
                        forwarded
                      </span>
                    )}
                    {it.replied && (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#CCFF00] text-black">
                        replied
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-neutral-500 mt-3 line-clamp-2">{it.body}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    onClick={() => draftReply(it)}
                    size="sm"
                    className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
                    data-testid={`inbox-draft-reply-${it.id}`}
                  >
                    Draft reply →
                  </Button>
                  <Button
                    onClick={() => remove(it.id)}
                    size="sm"
                    variant="ghost"
                    className="rounded-full opacity-60 hover:opacity-100"
                    data-testid={`inbox-delete-${it.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
